
import React, { useState, useEffect } from "react";
import { Editor } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface LinkFormProps {
  editor: Editor;
  onClose: () => void;
}

const LinkForm = ({ editor, onClose }: LinkFormProps) => {
  const [url, setUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [title, setTitle] = useState("");

  useEffect(() => {
    // Get the selected text and any existing link attributes
    if (editor.isActive("link")) {
      const attrs = editor.getAttributes("link");
      setUrl(attrs.href || "");
      // Try to get title from dataset if available
      const selection = editor.view.state.selection;
      const node = editor.view.domAtPos(selection.from).node as HTMLElement;
      const linkElement = node.closest("a");
      if (linkElement) {
        setTitle(linkElement.dataset.title || "");
      }
    } else {
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, "");
      setLinkText(text);
    }
  }, [editor]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url) {
      // If there is no selection but we have link text, insert it
      if (editor.state.selection.empty && linkText) {
        editor.chain().focus().insertContent({
          type: "text",
          text: linkText,
        }).run();

        // First set the link with standard attributes
        editor.chain().focus().extendMarkRange("link").setLink({
          href: url,
          rel: "noopener noreferrer ugc"
        }).run();

        // Then if we have a title, set it as a data attribute via DOM
        if (title) {
          setTimeout(() => {
            const selection = editor.view.state.selection;
            const node = editor.view.domAtPos(selection.from).node as HTMLElement;
            const linkElement = node.closest("a");
            if (linkElement) {
              linkElement.dataset.title = title;
            }
          }, 0);
        }
      } else {
        // Update link or set new link on existing selection
        editor.chain().focus().extendMarkRange("link").setLink({
          href: url,
          rel: "noopener noreferrer ugc"
        }).run();

        // Then set the title attribute via DOM
        if (title) {
          setTimeout(() => {
            const selection = editor.view.state.selection;
            const node = editor.view.domAtPos(selection.from).node as HTMLElement;
            const linkElement = node.closest("a");
            if (linkElement) {
              linkElement.dataset.title = title;
            }
          }, 0);
        }
      }
    } else {
      // Remove link
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    }
    onClose();
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 pt-1">
      <div className="space-y-2">
        <Label htmlFor="link-url">URL</Label>
        <Input
          id="link-url"
          type="url"
          placeholder="https://example.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoFocus
        />
      </div>

      {editor.state.selection.empty && (
        <div className="space-y-2">
          <Label htmlFor="link-text">Link Text</Label>
          <Input
            id="link-text"
            type="text"
            placeholder="Display text"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="link-title">Title (tooltip)</Label>
        <Input
          id="link-title"
          type="text"
          placeholder="Link title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            onClose();
          }}
        >
          Remove
        </Button>
        <Button type="submit" size="sm">Apply</Button>
      </div>
    </form>
  );
};

export default LinkForm;
